// Student Editor - PartCPage2 (Pixel-perfect redesign with exact visual match)
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useActivityContext } from '../index';
import { API_CONFIG } from '../../../../../config/api';

const LABEL_WIDTH = 260; // px – fixed width for the left column in the top two boxes

const PartCPage2 = () => {
  const { activity } = useActivityContext();
  const [formData, setFormData] = useState({
    evidence_collection: '',
    analysis_synthesis: '',
    what_i_know: '',
    what_i_need_to_do: '',
    conclusion: '',
    discussions: '',
    research_task_schedule: {}
  });
  const [saveMessage, setSaveMessage] = useState('');

  useEffect(() => {
    if (activity?.my_response_part_c?.page_2) {
      setFormData(activity.my_response_part_c.page_2);
    }
  }, [activity]);

  const saveChanges = async (data) => {
    try {
      const token = localStorage.getItem('accessToken');
      const apiUrl = API_CONFIG.BASE_URL;

      await axios.put(
        `${apiUrl}/secondary/user/domain-activities/${activity.activity_id}/part-c/page/2/student-fields`,
        data,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setSaveMessage('Saved ✓');
      setTimeout(() => setSaveMessage(''), 2000);
    } catch (error) {
      console.error('Error:', error);
      setSaveMessage('Error');
    }
  };

  const handleChange = (field, value) => {
    const newData = { ...formData, [field]: value };
    setFormData(newData);
    saveChanges(newData);
  };

  const handleScheduleChange = (day, value) => {
    const newSchedule = { ...formData.research_task_schedule, [`day_${day}`]: value };
    const newData = { ...formData, research_task_schedule: newSchedule };
    setFormData(newData);
    saveChanges(newData);
  };

  const ChevronFullBox = ({ labelText, children, showArrow = false }) => (
    <div style={{ position: 'relative', marginBottom: '10px' }}>
      {showArrow && (
        <div style={{
          position: 'absolute',
          left: '50%',
          top: '-22px',
          transform: 'translateX(-50%)',
          fontSize: '28px',
          color: '#4A90A4',
          fontWeight: 'bold'
        }}>
          ↓
        </div>
      )}
      <div style={{ display: 'flex', alignItems: 'stretch' }}>
        {/* LEFT COLUMN WITH FIXED WIDTH */}
        <div style={{
          backgroundColor: '#4A90A4',
          color: 'white',
          padding: '15px 20px',
          fontSize: '14px',
          fontWeight: 'bold',
          borderRadius: '8px 0 0 8px',
          boxSizing: 'border-box',

          // Hard-fix width so both rows are identical
          flex: `0 0 ${LABEL_WIDTH}px`,
          width: `${LABEL_WIDTH}px`,
          minWidth: `${LABEL_WIDTH}px`,
          maxWidth: `${LABEL_WIDTH}px`,

          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          textAlign: 'center',
          lineHeight: '1.3',
          overflowWrap: 'break-word',
          wordWrap: 'break-word',
          hyphens: 'auto'
        }}>
          {labelText}
        </div>

        {/* RIGHT COLUMN */}
        <div style={{
          backgroundColor: '#B3D9E8',
          flex: 1,
          borderRadius: '0 8px 8px 0',
          position: 'relative'
        }}>
          {children}
        </div>
      </div>
    </div>
  );

  const ChevronHalfBox = ({ labelText, children }) => (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <div style={{
        backgroundColor: '#4A90A4',
        color: 'white',
        padding: '10px 15px',
        fontSize: '14px',
        fontWeight: 'bold',
        textAlign: 'center',
        lineHeight: '1.3'
      }}>
        {labelText}
      </div>
      <div style={{
        backgroundColor: '#B3D9E8',
        flex: 1,
        border: '2px solid #4A90A4',
        borderTop: 'none',
        position: 'relative'
      }}>
        {children}
      </div>
    </div>
  );

  return (
    <div style={{
      width: '100%',
      maxWidth: '1400px',
      minHeight: '80vh',
      margin: '0 auto',
      padding: '40px',
      backgroundColor: 'white',
      boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
      position: 'relative',
      fontFamily: "'Poppins', Arial, sans-serif"
    }}>
      {saveMessage && (
        <div style={{
          position: 'fixed',
          top: '20px',
          right: '20px',
          backgroundColor: saveMessage.includes('Error') ? '#f44336' : '#4caf50',
          color: 'white',
          padding: '8px 16px',
          borderRadius: '4px',
          zIndex: 1000
        }}>
          {saveMessage}
        </div>
      )}

      {/* Evidence collection */}
      <ChevronFullBox labelText={<>Evidence collection to<br />support/negate hypothesis</>}>
        <textarea
          value={formData.evidence_collection || ''}
          onChange={(e) => handleChange('evidence_collection', e.target.value)}
          placeholder="Describe the evidence collected..."
          style={{
            width: '100%',
            height: '100%',
            minHeight: '100px',
            border: 'none',
            padding: '12px 15px',
            fontSize: '10px',
            fontFamily: "'Poppins', Arial, sans-serif",
            resize: 'vertical',
            boxSizing: 'border-box',
            backgroundColor: 'transparent'
          }}
        />
      </ChevronFullBox>

      {/* Analysis and Synthesis */}
      <ChevronFullBox labelText="Analysis and Synthesis" showArrow>
        <textarea
          value={formData.analysis_synthesis || ''}
          onChange={(e) => handleChange('analysis_synthesis', e.target.value)}
          placeholder="Analyze and synthesize the findings..."
          style={{
            width: '100%',
            height: '100%',
            minHeight: '100px',
            border: 'none',
            padding: '12px 15px',
            fontSize: '10px',
            fontFamily: "'Poppins', Arial, sans-serif",
            resize: 'vertical',
            boxSizing: 'border-box',
            backgroundColor: 'transparent'
          }}
        />
      </ChevronFullBox>

      {/* Arrow before side-by-side boxes */}
      <div style={{ textAlign: 'center', margin: '-5px 0 5px 0' }}>
        <span style={{ fontSize: '28px', color: '#4A90A4', fontWeight: 'bold' }}>↓</span>
      </div>

      {/* What I know & What I need to do */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px', marginBottom: '10px', position: 'relative' }}>
        <ChevronHalfBox labelText="What do I know?">
          <textarea
            value={formData.what_i_know || ''}
            onChange={(e) => handleChange('what_i_know', e.target.value)}
            placeholder="What I already know..."
            style={{
              width: '100%',
              height: '100%',
              minHeight: '120px',
              border: 'none',
              padding: '12px 15px',
              fontSize: '10px',
              fontFamily: "Poppins, Arial, sans-serif",
              resize: 'vertical',
              boxSizing: 'border-box',
              backgroundColor: 'transparent'
            }}
          />
        </ChevronHalfBox>
        <ChevronHalfBox labelText={<>What do I need to do?<br />What do I need to find out?</>}>
          <textarea
            value={formData.what_i_need_to_do || ''}
            onChange={(e) => handleChange('what_i_need_to_do', e.target.value)}
            placeholder="What I need to do and find out..."
            style={{
              width: '100%',
              height: '100%',
              minHeight: '120px',
              border: 'none',
              padding: '12px 15px',
              fontSize: '10px',
              fontFamily: "'Poppins', Arial, sans-serif",
              resize: 'vertical',
              boxSizing: 'border-box',
              backgroundColor: 'transparent'
            }}
          />
        </ChevronHalfBox>
        <div style={{
          position: 'absolute',
          left: '50%',
          top: '50%',
          transform: 'translate(-50%, -50%)',
          fontSize: '28px',
          color: '#4A90A4',
          fontWeight: 'bold',
          zIndex: 1,
          backgroundColor: 'white',
          padding: '0 5px'
        }}>
          →
        </div>
      </div>

      {/* Arrow pointing down */}
      <div style={{ textAlign: 'center', margin: '-5px 0 5px 0' }}>
        <span style={{ fontSize: '28px', color: '#4A90A4', fontWeight: 'bold' }}>↓</span>
      </div>

      {/* Conclusion & Discussions */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px', marginBottom: '15px', position: 'relative' }}>
        <ChevronHalfBox labelText="Conclusion">
          <textarea
            value={formData.conclusion || ''}
            onChange={(e) => handleChange('conclusion', e.target.value)}
            placeholder="Write your conclusion..."
            style={{
              width: '100%',
              height: '100%',
              minHeight: '120px',
              border: 'none',
              padding: '12px 15px',
              fontSize: '10px',
              fontFamily: "'Poppins', Arial, sans-serif",
              resize: 'vertical',
              boxSizing: 'border-box',
              backgroundColor: 'transparent'
            }}
          />
        </ChevronHalfBox>
        <ChevronHalfBox labelText="Discussions">
          <textarea
            value={formData.discussions || ''}
            onChange={(e) => handleChange('discussions', e.target.value)}
            placeholder="Discuss your findings..."
            style={{
              width: '100%',
              height: '100%',
              minHeight: '120px',
              border: 'none',
              padding: '12px 15px',
              fontSize: '10px',
              fontFamily: "'Poppins', Arial, sans-serif",
              resize: 'vertical',
              boxSizing: 'border-box',
              backgroundColor: 'transparent'
            }}
          />
        </ChevronHalfBox>
        <div style={{
          position: 'absolute',
          right: '52%',
          top: '50%',
          transform: 'translateY(-50%)',
          fontSize: '28px',
          color: '#4A90A4',
          fontWeight: 'bold',
          zIndex: 1,
          backgroundColor: 'white',
          padding: '0 5px'
        }}>
          ←
        </div>
      </div>

      {/* Research task schedule */}
      <div style={{ marginBottom: '20px' }}>
        <div style={{ display: 'flex', alignItems: 'stretch' }}>
          <div style={{
            backgroundColor: '#4A90A4',
            color: 'white',
            padding: '20px 15px',
            fontSize: '12px',
            fontWeight: 'bold',
            minWidth: '180px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            textAlign: 'center'
          }}>
            Research task schedule
          </div>
          <div style={{
            backgroundColor: '#B3D9E8',
            flex: 1,
            padding: '12px 15px',
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: '6px 30px',
            fontSize: '10px',
            alignContent: 'center'
          }}>
            {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(day => (
              <div key={day} style={{ display: 'flex', gap: '5px', alignItems: 'center' }}>
                <strong style={{ minWidth: '42px' }}>Day {day}:</strong>
                <input
                  type="text"
                  value={formData.research_task_schedule?.[`day_${day}`] || ''}
                  onChange={(e) => handleScheduleChange(day, e.target.value)}
                  placeholder="-"
                  style={{
                    flex: 1,
                    padding: '3px 6px',
                    border: '1px solid #4A90A4',
                    borderRadius: '3px',
                    fontSize: '10px',
                    fontFamily: "'Poppins', Arial, sans-serif",
                    backgroundColor: 'white'
                  }}
                />
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PartCPage2;
